// Hook para la lógica de reserva de turnos de imágenes
// Basado en useNuevoTurno.js, adaptado para estudios

import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router";
import dayjs from "dayjs";
import { traerPrestadoresImagenes } from "./prestadoresServiceImagenes";
import { procesarPrestadoresImagenes } from "./nuevoTurnoImagenesUtils";
import {
  getHorariosPrestadores,
  traerTurnosPaciente,
} from "../../apiClient/rutes/turnos";
import { getMutualPorCodigo } from "../../apiClient/rutes/paciente";
import {
  obtenerHorariosYTurnosImagenes,
  manejarReservaTurnoImagenes,
} from "./turnosServiceImagenes";
import {
  verificarInasistenciasPorEstudio,
  verificarTurnoConPrestadorPendiente,
} from "./nuevoTurnoImagenesUtils";

export const useNuevoTurnoImagenes = (mostrarNotificacion) => {
  const [ui, setUI] = useState({
    cargando: true,
    abrirDesplegableEstudios: false,
    abrirDesplegablePrestadores: false,
    abrirModalConfirmarReserva: false,
    abrirModalPostReserva: false,
    mostrarInasisEstudio: false,
    mostrarTurnoPrestadorReservado: false,
  });
  const [usuario, setUsuario] = useState({});
  const [urlAxioPP, setUrlAxio] = useState("");
  const [estudios, setEstudios] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [listadoPrestadores, setListadoPrestadores] = useState([]);
  const [estudioSeleccionado, setEstudioSeleccionado] = useState(-1);
  const [prestadorSeleccionado, setPrestadorSeleccionado] = useState(-1);
  const [fechasFuturas, setFechasFuturas] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState("");
  const [turnosPaciente, setTurnosPaciente] = useState([]);
  const [turnosDisponibles, setTurnosDisponibles] = useState([]);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState({});
  const [notaWebMutual, setNotaWebMutual] = useState("");
  const history = useHistory();

  const setLoading = useCallback(
    (cargando) => setUI((ui) => ({ ...ui, cargando })),
    [],
  );
  const setModal = useCallback(
    (modal, value) => setUI((ui) => ({ ...ui, [modal]: value })),
    [],
  );
  const setFeedback = useCallback(
    (feedback, value) => setUI((ui) => ({ ...ui, [feedback]: value })),
    [],
  );

  const obtenerTurnosPrestador = useCallback(
    async (prestador) => {
      setLoading(true);
      try {
        const resultado = await obtenerHorariosYTurnosImagenes(
          prestador,
          usuario,
          urlAxioPP,
        );
        if (resultado.success) {
          setFechasFuturas(resultado.turnosProximos);
          setTurnosDisponibles(resultado.turnosDisponibles);
          setFechaSeleccionada(resultado.fechaSeleccionada);
          if (
            !resultado.turnosDisponibles ||
            resultado.turnosDisponibles.length === 0
          ) {
            mostrarNotificacion(
              true,
              "No hay turnos disponibles con el prestador seleccionado",
              "amarillo",
            );
          }
        } else {
          mostrarNotificacion(
            true,
            resultado.error || "No se pudieron cargar los horarios disponibles",
            "rojo",
          );
          setFechasFuturas([]);
          setTurnosDisponibles([]);
          setFechaSeleccionada("");
        }
      } catch (error) {
        mostrarNotificacion(true, "Error inesperado: " + error.message, "rojo");
      }
      setLoading(false);
    },
    [usuario, urlAxioPP, mostrarNotificacion, setLoading],
  );

  const estudioModalAbierto = useRef(false);

  const handleChangeSelect = useCallback(
    (value, select) => {
      if (select === "Estudio") {
        setEstudioSeleccionado(value);
        if (value !== -1) {
          let filtrados = prestadores
            .filter(
              (prestador) =>
                prestador.estudio === value || prestador.estudio2 === value,
            )
            .map((p) => ({
              ...p,
              text: p.nombre || p.text || p.label || "",
            }));
          const prestadoresOrdenados =
            require("./nuevoTurnoImagenesUtils").ordenarPrestadoresPorProximoTurno(
              filtrados,
            );
          setListadoPrestadores(prestadoresOrdenados);
          if (verificarInasistenciasPorEstudio(value, turnosPaciente)) {
            setFechasFuturas([]);
            setFeedback("mostrarInasisEstudio", true);
            setFeedback("mostrarTurnoPrestadorReservado", false);
          } else {
            setFeedback("mostrarInasisEstudio", false);
          }
          setModal("abrirDesplegablePrestadores", true);
        } else {
          setListadoPrestadores(prestadores);
        }
        setPrestadorSeleccionado(-1);
        setFechasFuturas([]);
        return;
      }
      if (select === "Profesional") {
        let codigoValue = value;
        if (typeof value === "object" && value !== null) {
          codigoValue = value.codigo || value.id || "";
        }
        if (typeof codigoValue !== "string") {
          codigoValue = String(codigoValue);
        }
        const [prestadorCodigo] = codigoValue.split("-");
        if (
          verificarTurnoConPrestadorPendiente(prestadorCodigo, turnosPaciente)
        ) {
          setFechasFuturas([]);
          setFeedback("mostrarTurnoPrestadorReservado", true);
          setFeedback("mostrarInasisEstudio", false);
        } else {
          setFeedback("mostrarTurnoPrestadorReservado", false);
          setPrestadorSeleccionado(codigoValue);
          obtenerTurnosPrestador(codigoValue);
        }
        return;
      }
      mostrarNotificacion(true, "Selección inválida", "rojo");
      return;
    },
    [
      prestadores,
      turnosPaciente,
      obtenerTurnosPrestador,
      mostrarNotificacion,
      setFeedback,
      setModal,
    ],
  );

  const cargarDesplegables = useCallback(
    async (sesion) => {
      try {
        if (!sesion || !sesion.token || !sesion.mutual) {
          console.error("[cargarDesplegables] Sesion inválida:", sesion);
          mostrarNotificacion(
            true,
            "Error: sesión inválida. No se puede continuar.",
            "rojo",
          );
          setLoading(false);
          return;
        }
        // Traer estudios desde la API
        //const response = await fetch("https://portal.institutomedicoprivadovcp-pacientes.com.ar:8182/api/v2/Estudios");
        const response = await fetch(
          "http://201.231.113.236:8183/api/v2/Estudios",
        );

        const estudiosData = await response.json();
        setEstudios(estudiosData.map((e) => ({ ...e, text: e.nombre })));
        // Traer prestadores
        const horariosPrestadores = await getHorariosPrestadores(sesion);
        const prestadoresImagenes = await traerPrestadoresImagenes(
          sesion,
          horariosPrestadores,
        );
        const mutualData = await getMutualPorCodigo(
          sesion.mutual,
          sesion.token,
        );
        setPrestadores(prestadoresImagenes);
        setListadoPrestadores(prestadoresImagenes);
        setNotaWebMutual(
          (mutualData?.NotaWeb || mutualData?.notaWeb || "").trim(),
        );
      } catch (error) {
        console.error("[cargarDesplegables] Error:", error);
        mostrarNotificacion(
          true,
          "Error al cargar datos: " + error.message,
          "rojo",
        );
      }
      setLoading(false);
    },
    [mostrarNotificacion, setLoading],
  );

  const cargaInicial = useRef(false);
  useEffect(() => {
    if (cargaInicial.current) return;
    cargaInicial.current = true;
    const inicializar = async () => {
      try {
        const sesion = JSON.parse(sessionStorage.getItem("ppUL"));
        if (sesion) {
          await Promise.all([
            traerTurnosPaciente(sesion).then((turnos) =>
              setTurnosPaciente(turnos),
            ),
            cargarDesplegables(sesion),
          ]);
          setUsuario(sesion);
          setUrlAxio(localStorage.getItem("urlAxioPP"));
          document.title = localStorage.getItem("tituloWeb");
        }
      } catch (e) {
        history.push({ pathname: "/ErrorPage" });
      }
    };
    inicializar();
  }, [cargarDesplegables, history]);

  const seleccionInicial = useRef(false);
  const prestadorSeleccionadoExternamente = useRef(false);
  useEffect(() => {
    if (
      !seleccionInicial.current &&
      estudios.length > 0 &&
      prestadores.length > 0 &&
      prestadorSeleccionado === -1
    ) {
      const prestadorExterno = history.location.state?.prestador;
      if (prestadorExterno !== undefined && prestadorExterno !== null) {
        const prestadorEncontrado = prestadores.find(
          (prestador) =>
            prestador.codigo.split("-")[0] === prestadorExterno.toString(),
        );
        if (prestadorEncontrado) {
          seleccionInicial.current = true;
          prestadorSeleccionadoExternamente.current = true;
          setEstudioSeleccionado(prestadorEncontrado.estudio);
          const prestadoresFiltrados = prestadores.filter(
            (prestador) =>
              prestador.estudio === prestadorEncontrado.estudio ||
              prestador.estudio2 === prestadorEncontrado.estudio,
          );
          setListadoPrestadores(prestadoresFiltrados);
          setPrestadorSeleccionado(prestadorEncontrado.codigo);
          if (verificarInasistenciasPorEstudio) {
            const turnosSafe = Array.isArray(turnosPaciente)
              ? turnosPaciente
              : [];
            if (
              verificarInasistenciasPorEstudio(
                prestadorEncontrado.estudio,
                turnosSafe,
              )
            ) {
              setFechasFuturas([]);
              setFeedback("mostrarInasisEstudio", true);
              setFeedback("mostrarTurnoPrestadorReservado", false);
            } else {
              setFeedback("mostrarInasisEstudio", false);
              if (verificarTurnoConPrestadorPendiente) {
                if (
                  verificarTurnoConPrestadorPendiente(
                    prestadorEncontrado.codigo,
                    turnosSafe,
                  )
                ) {
                  setFechasFuturas([]);
                  setFeedback("mostrarTurnoPrestadorReservado", true);
                  setFeedback("mostrarInasisEstudio", false);
                } else {
                  setFeedback("mostrarTurnoPrestadorReservado", false);
                  obtenerTurnosPrestador(prestadorEncontrado.codigo);
                }
              }
            }
          }
          mostrarNotificacion(
            true,
            `Cargando horarios para ${prestadorEncontrado.nombre || prestadorEncontrado.text || prestadorEncontrado.label}...`,
            "verde",
          );
        } else {
          mostrarNotificacion(
            true,
            "El prestador seleccionado no está disponible, seleccione un estudio",
            "amarillo",
          );
          setModal("abrirDesplegableEstudios", true);
        }
        history.location.state = {};
      } else {
        if (estudioSeleccionado === -1 && !estudioModalAbierto.current) {
          estudioModalAbierto.current = true;
          setModal("abrirDesplegableEstudios", true);
        }
        prestadorSeleccionadoExternamente.current = false;
      }
    }
  }, [
    prestadores,
    estudios,
    history,
    prestadorSeleccionado,
    estudioSeleccionado,
    mostrarNotificacion,
    setModal,
    setEstudioSeleccionado,
    setListadoPrestadores,
    setPrestadorSeleccionado,
    setFeedback,
    setFechasFuturas,
    obtenerTurnosPrestador,
    turnosPaciente,
  ]);

  const handleClickConfirmarHacerReserva = useCallback(async () => {
    setLoading(true);
    try {
      const resultado = await manejarReservaTurnoImagenes(
        turnoSeleccionado,
        usuario,
        prestadorSeleccionado,
        urlAxioPP,
      );
      if (resultado.success) {
        mostrarNotificacion(
          true,
          resultado.mensaje || "El turno se reservó correctamente",
          "verde",
        );
        setModal("abrirModalConfirmarReserva", false);
        setFechaSeleccionada("");
        setTurnosDisponibles([]);
        setModal("abrirModalPostReserva", true);
      } else {
        mostrarNotificacion(
          true,
          resultado.mensaje ||
            "Ocurrió un problema al intentar reservar el turno",
          "rojo",
        );
      }
    } catch (error) {
      mostrarNotificacion(true, "Error inesperado: " + error.message, "rojo");
    }
    setLoading(false);
  }, [
    turnoSeleccionado,
    usuario,
    prestadorSeleccionado,
    urlAxioPP,
    mostrarNotificacion,
    setLoading,
    setModal,
  ]);

  const handleClickSeleccionarTurno = useCallback(
    (turno) => {
      setTurnoSeleccionado(turno);
      setModal("abrirModalConfirmarReserva", true);
    },
    [setModal],
  );

  const handleChangeFecha = useCallback((value) => {
    setFechaSeleccionada(value);
  }, []);

  const handleClickCopiarTelefono = useCallback(() => {
    const phoneNumber = "08105552765";
    navigator.clipboard.writeText(phoneNumber);
    mostrarNotificacion(
      true,
      "Se ha copiado el número de teléfono a su portapapeles",
      "",
    );
  }, [mostrarNotificacion]);

  const handlePostTurnoReservado = useCallback(
    (accion) => {
      setTurnoSeleccionado({});
      setModal("abrirModalPostReserva", false);
      switch (accion) {
        case "nuevoTurno":
          window.location.reload();
          break;
        case "turnosPaciente":
          history.push({ pathname: "/portal/Turnos" });
          break;
        default:
          history.push({ pathname: "/portal/Turnos" });
          break;
      }
    },
    [setModal, history],
  );

  const renderTurnos = useCallback(() => {
    const turnos = turnosDisponibles.filter(
      (turno) =>
        dayjs(turno.fecha).format("YYYY-MM-DD") ===
        dayjs(fechaSeleccionada).format("YYYY-MM-DD"),
    );
    return turnos.map((turno) => (
      <button
        key={turno.codigo}
        onClick={() => handleClickSeleccionarTurno(turno)}
        className="turno-button"
      >
        {dayjs(turno.hora).format("HH:mm")}
      </button>
    ));
  }, [turnosDisponibles, fechaSeleccionada, handleClickSeleccionarTurno]);

  const getPrestadorSeleccionadoData = useCallback(() => {
    return (
      prestadores.find(
        (prestador) => prestador.codigo === prestadorSeleccionado,
      ) || {}
    );
  }, [prestadores, prestadorSeleccionado]);

  const estudiosDesplegable = estudios.map((e) => ({
    codigo: e.codigo ?? e.id ?? "",
    text: e.text ?? e.nombre ?? e.label ?? "",
    ...e,
  }));
  const prestadoresDesplegable = listadoPrestadores.map((p) => ({
    codigo: p.codigo ?? p.id ?? "",
    text: p.text ?? p.nombre ?? p.label ?? "",
    ...p,
  }));

  return {
    usuario,
    cargando: ui.cargando,
    estudios,
    prestadores,
    listadoPrestadores,
    estudiosDesplegable,
    prestadoresDesplegable,
    estudioSeleccionado,
    prestadorSeleccionado,
    fechasFuturas,
    fechaSeleccionada,
    turnosDisponibles,
    turnoSeleccionado,
    notaWebMutual,
    mostrarInasisEstudio: ui.mostrarInasisEstudio,
    mostrarTurnoPrestadorReservado: ui.mostrarTurnoPrestadorReservado,
    abrirDesplegableEstudios: ui.abrirDesplegableEstudios,
    abrirDesplegablePrestadores: ui.abrirDesplegablePrestadores,
    abrirModalConfirmarReserva: ui.abrirModalConfirmarReserva,
    abrirModalPostReserva: ui.abrirModalPostReserva,
    setEstudioSeleccionado,
    setPrestadorSeleccionado,
    setFechaSeleccionada,
    setTurnoSeleccionado,
    setAbrirDesplegableEstudios: (valor) =>
      setUI((ui) => ({ ...ui, abrirDesplegableEstudios: valor })),
    setAbrirDesplegablePrestadores: (valor) =>
      setUI((ui) => ({ ...ui, abrirDesplegablePrestadores: valor })),
    setAbrirModalConfirmarReserva: (valor) =>
      setUI((ui) => ({ ...ui, abrirModalConfirmarReserva: valor })),
    setAbrirModalPostReserva: (valor) =>
      setUI((ui) => ({ ...ui, abrirModalPostReserva: valor })),
    handleChangeSelect,
    handleChangeFecha,
    handleClickSeleccionarTurno,
    handleClickConfirmarHacerReserva,
    handleClickCopiarTelefono,
    handlePostTurnoReservado,
    renderTurnos,
    getPrestadorSeleccionadoData,
    setModal,
    setFeedback,
  };
};
